// src/component/dashboard/ProgressPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Search, Globe, TrendingUp, Users, Mail, BarChart3 } from 'lucide-react';

const ProgressPopup = ({ isVisible, brandName, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30);

  // Refs to track timers independently
  const progressIntervalRef = useRef(null);
  const messageTimeoutRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const progressMessages = [
    {
      icon: <Search className="w-5 h-5" />,
      text: `Working on your brand "${brandName}"...`,
      shortText: "Working on brand",
      duration: 8000
    },
    {
      icon: <Globe className="w-5 h-5" />,
      text: "Analyzing domain and gathering data...",
      shortText: "Analyzing domain",
      duration: 10000
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      text: "Finding potential affiliates...",
      shortText: "Finding affiliates",
      duration: 12000
    },
    {
      icon: <Users className="w-5 h-5" />,
      text: "Scanning competitor networks...",
      shortText: "Scanning networks",
      duration: 8000
    },
    {
      icon: <Mail className="w-5 h-5" />,
      text: "Collecting contact information...",
      shortText: "Collecting contacts",
      duration: 10000
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      text: "Processing traffic and keyword data...",
      shortText: "Processing data",
      duration: 8000
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      text: "Finalizing your affiliate prospects...",
      shortText: "Finalizing results",
      duration: 4000
    }
  ];

  useEffect(() => {
    if (!isVisible) {
      // Reset all state when popup becomes hidden
      setProgress(0);
      setCurrentMessageIndex(0);
      setIsCompleted(false);
      setTimeRemaining(30);
      return;
    }

    // Clear any existing timers
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    if (autoCloseTimeoutRef.current) clearTimeout(autoCloseTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Reset state for fresh start
    setProgress(0);
    setCurrentMessageIndex(0);
    setIsCompleted(false);
    setTimeRemaining(30);

    // Progress bar animation (30 seconds total)
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / 300); // 300 intervals for 30 seconds (100ms each)
        if (newProgress >= 100) {
          setIsCompleted(true);
          return 100;
        }
        return newProgress;
      });
    }, 100);

    // Countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    // Auto close after exactly 30 seconds
    autoCloseTimeoutRef.current = setTimeout(() => {
      setIsCompleted(true);
      // Small delay to show completion state
      setTimeout(() => {
        onComplete();
      }, 500);
    }, 30000);

    // Message rotation (faster for 30 seconds)
    let currentIndex = 0;
    const rotateMessage = () => {
      if (currentIndex < progressMessages.length - 1) {
        // Adjust duration to fit in 30 seconds
        const adjustedDuration = Math.min(progressMessages[currentIndex].duration, 4000);
        messageTimeoutRef.current = setTimeout(() => {
          currentIndex++;
          setCurrentMessageIndex(currentIndex);
          rotateMessage();
        }, adjustedDuration);
      }
    };
    rotateMessage();

    // Cleanup function
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      if (autoCloseTimeoutRef.current) clearTimeout(autoCloseTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  if (!isVisible) return null;

  const currentMessage = progressMessages[currentMessageIndex];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-md p-6">
        <div className="text-center">
          {/* Header */}
          <div className="mb-6">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              {isCompleted ? (
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              ) : (
                <div className="relative">
                  {currentMessage.icon && (
                    <div className="text-blue-600 dark:text-blue-400 animate-pulse">
                      {currentMessage.icon}
                    </div>
                  )}
                </div>
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100 mb-2">
              {isCompleted ? 'Processing Complete!' : 'Processing Your Brand'}
            </h3>
          </div>

          {/* Progress Message */}
          <div className="mb-6 min-h-[24px]">
            <p className="text-sm text-gray-600 dark:text-slate-400 flex items-center justify-center gap-2">
              {!isCompleted && currentMessage.icon && (
                <span className="text-blue-600 dark:text-blue-400">
                  {currentMessage.icon}
                </span>
              )}
              <span className="transition-all duration-500 ease-in-out">
                {isCompleted ?
                  `Successfully processed "${brandName}" and found potential affiliates!` :
                  currentMessage.text
                }
              </span>
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all duration-100 ease-out rounded-full ${
                  isCompleted ? 'bg-green-500 dark:bg-green-400' : 'bg-blue-600 dark:bg-blue-500'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-slate-400">
              {isCompleted ? (
                <span className="text-green-600 dark:text-green-400 font-medium">100% Complete</span>
              ) : (
                `${Math.round(progress)}% • ${timeRemaining} seconds remaining`
              )}
            </div>
          </div>

          {/* Status Indicators */}
          <div className="space-y-2 text-xs">
            {progressMessages.slice(0, 6).map((message, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                  index <= currentMessageIndex || isCompleted
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : index === currentMessageIndex + 1
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 dark:bg-slate-700 text-gray-400 dark:text-slate-500'
                }`}
              >
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  index <= currentMessageIndex || isCompleted
                    ? 'bg-green-500 dark:bg-green-400'
                    : index === currentMessageIndex + 1
                    ? 'bg-blue-500 dark:bg-blue-400'
                    : 'bg-gray-300 dark:bg-slate-600'
                }`}>
                  {index <= currentMessageIndex || isCompleted ? (
                    <CheckCircle className="w-3 h-3 text-white" />
                  ) : (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className="text-left flex-1">
                  {message.shortText}
                </span>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          {!isCompleted && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <span className="font-medium">Please wait</span> - We're analyzing millions of data points to find the best affiliate opportunities for your brand.
              </p>
            </div>
          )}

          {isCompleted && (
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-xs text-green-700 dark:text-green-300">
                <span className="font-medium">Success!</span> Your brand has been processed and affiliate prospects are ready for review.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressPopup;